// Sprint 2 PR-D2 — Central Brain Conversations view (read-only, lightweight list).
// Endpoint: GET /api/v2/conversations (list with messageCount, no messages payload)
// Row click → conversation-drawer fetches /api/v2/conversations/:id (full messages).

import { escHtml } from '../utils.js';
import { apiGetJson } from '../api.js';
import { openConversationDrawer } from '../ui/conversation-drawer.js';

export interface ConversationRow {
  id: string;
  tenantId: string;
  channel: string;
  externalContactId: string | null;
  leadId: string | null;
  messageCount: number;
  aiSummary: string | null;
  lastMessageAt: string | null;
  handoffRequired: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationsSummary { total: number; byChannel: Record<string, number>; byStatus: Record<string, number>; }
interface ConversationsResponse { ok: true; conversations: ConversationRow[]; summary: ConversationsSummary; }
export interface ConversationsFilters { tenantId?: string; channel?: string; status?: string; leadId?: string; limit?: number; }

type ViewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; conversations: ConversationRow[]; summary: ConversationsSummary; filters: ConversationsFilters }
  | { kind: 'error'; message: string; filters: ConversationsFilters };

let state: ViewState = { kind: 'idle' };
let filters: ConversationsFilters = { limit: 100 };

export async function loadCbConversations(): Promise<void> {
  state = { kind: 'loading' };
  const r = await apiGetJson<ConversationsResponse>('/api/v2/conversations', filters as Record<string, string | number | undefined>);
  if (r.ok) state = { kind: 'ready', conversations: r.data.conversations, summary: r.data.summary, filters: { ...filters } };
  else state = { kind: 'error', message: r.message ?? r.error, filters: { ...filters } };
}

export function renderCbConversations(): void {
  const root = document.getElementById('view-cb-conversations');
  if (!root) return;
  root.innerHTML = renderCbConversationsHtml(state);
  attachHandlers();
}

export function renderCbConversationsHtml(s: ViewState): string {
  const f = (s.kind === 'ready' || s.kind === 'error') ? s.filters : filters;
  const header = `
    <div class="view-header">
      <div>
        <div class="view-title">Conversations</div>
        <div class="view-sub">central_brain.conversations — read-only; row click → detail drawer</div>
      </div>
    </div>
    <div class="rt-card" style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;">
      ${selectFilter('channel', 'Channel', f.channel, ['whatsapp','email','web_form','instagram_dm','manual'])}
      ${selectFilter('status', 'Status', f.status, ['open','waiting_customer','waiting_agent','closed','archived'])}
      ${inputFilter('tenantId', 'Tenant', f.tenantId)}
      ${inputFilter('leadId', 'Lead ID', f.leadId)}
      <button id="cbc-apply" class="btn-primary" style="padding:6px 12px;">Filtrele</button>
    </div>`;
  if (s.kind === 'idle' || s.kind === 'loading') return `${header}<div class="empty-state"><div class="empty-icon">⏳</div><div class="empty-title">Yükleniyor…</div></div>`;
  if (s.kind === 'error') return `${header}<div class="rt-card" style="border-left:3px solid var(--danger,#e55);"><div class="rt-card-title">Yüklenemedi</div><div style="color:var(--muted);font-size:13px;">${escHtml(s.message)}</div></div>`;
  const { conversations, summary } = s;
  return `${header}
    <div class="rt-card" style="display:flex;flex-wrap:wrap;gap:14px;font-size:12px;">
      <span><strong>${summary.total}</strong> total</span>
      ${chips('channel', summary.byChannel)}
      ${chips('status', summary.byStatus)}
    </div>
    <div class="rt-card">
      <div class="rt-card-title">Conversations (${conversations.length})</div>
      ${conversations.length === 0
        ? '<div class="empty-state"><div class="empty-icon">📭</div><div class="empty-title">Eşleşen konuşma yok</div></div>'
        : `<table style="width:100%;font-size:13px;">
            <thead><tr>
              <th style="text-align:left;">Channel</th><th style="text-align:left;">Tenant</th>
              <th style="text-align:left;">Contact</th><th style="text-align:left;">Msgs</th>
              <th style="text-align:left;">Status</th><th style="text-align:left;">Last msg</th>
            </tr></thead>
            <tbody>${conversations.map(c => `
              <tr data-action="open-conversation" data-conversation-id="${escHtml(c.id)}" style="cursor:pointer;">
                <td><code style="background:var(--bg2);padding:2px 6px;border-radius:4px;font-size:11px;">${escHtml(c.channel)}</code></td>
                <td>${escHtml(c.tenantId)}</td>
                <td style="font-size:11px;">${escHtml(c.externalContactId ?? '—')}</td>
                <td style="text-align:center;">${c.messageCount}</td>
                <td>${escHtml(c.status)}</td>
                <td style="color:var(--muted);font-size:12px;">${escHtml(c.lastMessageAt ?? c.createdAt)}</td>
              </tr>`).join('')}
            </tbody>
          </table>`
      }
    </div>`;
}

function chips(label: string, m: Record<string, number>): string {
  return `<span><span style="color:var(--muted);">${escHtml(label)}:</span> ${Object.entries(m).map(([k,v]) => `<span style="background:var(--bg2);padding:2px 6px;border-radius:8px;margin-right:4px;">${escHtml(k)} <strong>${v}</strong></span>`).join('')}</span>`;
}
function selectFilter(id: string, label: string, value: string | undefined, opts: string[]): string {
  return `<label style="font-size:11px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">${escHtml(label)}<select id="cbc-f-${id}" class="form-select" style="width:140px;padding:4px 8px;"><option value="">(any)</option>${opts.map(o => `<option value="${escHtml(o)}"${o===value?' selected':''}>${escHtml(o)}</option>`).join('')}</select></label>`;
}
function inputFilter(id: string, label: string, value: string | undefined): string {
  return `<label style="font-size:11px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">${escHtml(label)}<input id="cbc-f-${id}" class="form-input" value="${escHtml(value ?? '')}" style="width:140px;padding:4px 8px;" /></label>`;
}
function attachHandlers(): void {
  const btn = document.getElementById('cbc-apply');
  if (btn) {
    btn.addEventListener('click', async () => {
      const getV = (id: string) => (document.getElementById(`cbc-f-${id}`) as HTMLInputElement | HTMLSelectElement | null)?.value || undefined;
      filters = { channel: getV('channel'), status: getV('status'), tenantId: getV('tenantId'), leadId: getV('leadId'), limit: 100 };
      await loadCbConversations();
      renderCbConversations();
    });
  }
  // Row click delegation → drawer
  const root = document.getElementById('view-cb-conversations');
  if (!root) return;
  root.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    const row = target?.closest<HTMLElement>('[data-action="open-conversation"]');
    if (!row) return;
    const id = row.dataset.conversationId;
    if (id) void openConversationDrawer(id);
  });
}
